import { RelocationOrderTransportMethod, RelocationOrderTransportReason } from "@/api-schema-enums";
import { RelocationOrderFlowRouteType } from "@/common/api/relocation-order-flow/_helpers/default-relocation-order-flow.enums";
import { RelocationOrderFlowInitBodyMap } from "@/common/api/relocation-order-flow/_helpers/resolve-relocation-order-flow-init-path";
import { useRelocationOrderFlowInitMutation } from "@/common/api/relocation-order-flow/use-relocation-order-flow-init.mutation";
import { formatDateToApi } from "@/common/helpers/format-date";
import { addressFormValuesToAddressDto } from "@/components/address-form-fields/_helpers/address-form-values-to-address-dto";
import { RelocationOrderType, useRelocationOrderInitStore } from "./use-relocation-order-init-store";

const ROUTE_TYPE_MAP: Record<RelocationOrderType, RelocationOrderFlowRouteType> = {
  [RelocationOrderType.BranchToBranch]: RelocationOrderFlowRouteType.BranchToBranch,
  [RelocationOrderType.BranchToExternalAddress]: RelocationOrderFlowRouteType.BranchToExternalAddress,
  [RelocationOrderType.ExternalAddressToBranch]: RelocationOrderFlowRouteType.ExternalAddressToBranch,
};

export function useRelocationOrderInitSubmit() {
  const vehicle = useRelocationOrderInitStore((state) => state.vehicle);
  const transportMethod = useRelocationOrderInitStore((state) => state.transportMethod);
  const transportCompanyUuid = useRelocationOrderInitStore((state) => state.transportCompanyUuid);
  const relocationOrderType = useRelocationOrderInitStore((state) => state.relocationOrderType);
  const originExternalAddress = useRelocationOrderInitStore((state) => state.originExternalAddress);
  const destinationBranch = useRelocationOrderInitStore((state) => state.destinationBranch);
  const destinationExternalAddress = useRelocationOrderInitStore((state) => state.destinationExternalAddress);

  const flowRouteType = ROUTE_TYPE_MAP[relocationOrderType];
  const mutation = useRelocationOrderFlowInitMutation(flowRouteType, transportMethod);

  const mutateAsync = async (earliestPickupDate: Date | null, transportReason: RelocationOrderTransportReason) => {
    const isTransportCompanyTransportMethod = transportMethod === RelocationOrderTransportMethod.TransportCompany;

    const commonFields = {
      vehicleUuid: vehicle!.uuid,
      transportReason: transportReason,
      ...(!isTransportCompanyTransportMethod && {
        earliestPickupDate: formatDateToApi(earliestPickupDate!),
      }),
    };

    type Body = RelocationOrderFlowInitBodyMap[typeof flowRouteType][typeof transportMethod];

    let body: Body;

    switch (relocationOrderType) {
      case RelocationOrderType.BranchToBranch:
        body = {
          ...commonFields,
          destinationBranchUuid: destinationBranch!.uuid,
          ...(isTransportCompanyTransportMethod && { transportCompanyUuid: transportCompanyUuid! }),
        } as Body;
        break;

      case RelocationOrderType.BranchToExternalAddress:
        body = {
          ...commonFields,
          destinationAddress: addressFormValuesToAddressDto(destinationExternalAddress!),
          ...(isTransportCompanyTransportMethod && { transportCompanyUuid: transportCompanyUuid! }),
        } as Body;
        break;

      case RelocationOrderType.ExternalAddressToBranch:
        body = {
          ...commonFields,
          originAddress: addressFormValuesToAddressDto(originExternalAddress!),
          destinationBranchUuid: destinationBranch!.uuid,
          ...(isTransportCompanyTransportMethod && { transportCompanyUuid: transportCompanyUuid! }),
        } as Body;
        break;
    }

    return mutation.mutateAsync(body);
  };

  return {
    mutateAsync,
    isPending: mutation.isPending,
  };
}
